import { Download } from '@mui/icons-material'
import { Box, Stack, styled, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import BasicDatePicker from 'src/components/DateTimePicker'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getUTCDate } from 'src/utils/function'

type BillingHeaderProps = {
  dateFrom: Date | null
  dateTo: Date | null
  onDateFromChange: (d: Date | null) => void
  onDateToChange: (d: Date | null) => void
  onExportPdf: () => void
  isExporting?: boolean
  exportDisabled?: boolean
}

export const BillingHeader = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onExportPdf,
  isExporting,
  exportDisabled,
}: BillingHeaderProps) => {
  const theme = useTheme()
  const { t } = useTranslation('admin-license')

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      justifyContent="space-between"
      gap={2}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: getThemeColor(theme, MUIThemeColors.primaryDark) }}>
        {t('customerBilling')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.5} flexWrap="wrap">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            p: 1,
            bgcolor: getThemeColor(theme, MUIThemeColors.white),
            flexDirection: 'row',
          }}
        >
          <StyledBox>
            <BasicDatePicker
              name="billing-from"
              isWithoutAppForm
              label={t('from')}
              value={dateFrom}
              onChange={date => onDateFromChange(date ? (getUTCDate(new Date(date)) as Date) : null)}
            />
          </StyledBox>
          <StyledBox>
            <BasicDatePicker
              name="billing-to"
              isWithoutAppForm
              label={t('to')}
              value={dateTo}
              onChange={date => onDateToChange(date ? (getUTCDate(new Date(date)) as Date) : null)}
            />
          </StyledBox>
        </Box>
        <GridActionButton
          variant="contained"
          buttonText={t('exportPdf')}
          buttonIcon={<Download />}
          onClick={onExportPdf}
          disabled={isExporting || exportDisabled}
          bgColor={getThemeColor(theme, MUIThemeColors.primaryMain)}
          textColor={getThemeColor(theme, MUIThemeColors.white)}
          shadowWeight={0}
          height="36px"
        />
      </Stack>
    </Stack>
  )
}

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiFormControl-root': { margin: 0 },
  '& .MuiInputBase-root': { height: '36px', alignItems: 'center' },
}))
